// client/src/components/publishing/MediaImportModal.jsx
//
// Media import for New Post / Media Tools (Roadmap Phase 3.5): pick photos and
// videos from Google Drive (Google Picker) or export a Canva design, and add
// them to the post like an upload. The server downloads the files with the
// organization's own connection and stores them in its media
// (server/routes/integrations/googleDriveRoutes.js, canvaRoutes.js).
//
// Connecting opens the provider's sign-in in a popup; the popup tells this
// window when it is done (postMessage) and the status reloads.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaGoogleDrive, FaPalette } from 'react-icons/fa';
import {
  getDriveStatus, getDrivePickerConfig, importFromDrive, disconnectDrive, driveLoginUrl,
  getCanvaStatus, listCanvaDesigns, importFromCanva, disconnectCanva, canvaLoginUrl, errorText,
} from '../../services/publishingApi';
import './Publishing.css';

const PICKER_SRC = 'https://apis.google.com/js/api.js';

/** Load the Google Picker library once. */
function loadPicker() {
  if (window.google && window.google.picker) return Promise.resolve(window.google.picker);
  return new Promise((resolve, reject) => {
    const done = () => window.gapi.load('picker', { callback: () => resolve(window.google.picker), onerror: reject });
    if (window.gapi) { done(); return; }
    const s = document.createElement('script');
    s.src = PICKER_SRC;
    s.async = true;
    s.onload = done;
    s.onerror = () => reject(new Error('picker script'));
    document.body.appendChild(s);
  });
}

function apiOrigin() {
  try { return new URL(process.env.REACT_APP_BASE_URL || window.location.origin).origin; } catch { return window.location.origin; }
}

/** Open a sign-in popup; resolves when it reports back or is closed. */
function connectPopup(url, type) {
  return new Promise((resolve) => {
    const w = window.open(url, `${type}-connect`, 'width=560,height=700');
    let finished = false;
    const finish = (ok) => {
      if (finished) return;
      finished = true;
      window.removeEventListener('message', onMessage);
      clearInterval(timer);
      resolve(ok);
    };
    const onMessage = (e) => {
      if (e.origin !== apiOrigin() && e.origin !== window.location.origin) return;
      if (e.data && e.data.type === type) finish(Boolean(e.data.ok));
    };
    window.addEventListener('message', onMessage);
    const timer = setInterval(() => { if (!w || w.closed) finish(null); }, 700);
  });
}

function DrivePanel({ tenantId, eventId, onImported, t }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [failed, setFailed] = useState([]);

  const load = useCallback(() => getDriveStatus(tenantId).then(setStatus).catch(() => setStatus({ configured: false })), [tenantId]);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setError('');
    await connectPopup(driveLoginUrl(tenantId), 'GOOGLE_DRIVE_CONNECTED');
    load();
  };

  const pick = async () => {
    setError(''); setFailed([]); setBusy('pick');
    try {
      const cfg = await getDrivePickerConfig(tenantId);
      const picker = await loadPicker();
      const view = new picker.DocsView(picker.ViewId.DOCS_IMAGES_AND_VIDEOS).setIncludeFolders(true);
      const builder = new picker.PickerBuilder()
        .addView(view)
        .enableFeature(picker.Feature.MULTISELECT_ENABLED)
        .setOAuthToken(cfg.accessToken)
        .setDeveloperKey(cfg.apiKey)
        .setOrigin(window.location.origin)
        .setCallback(async (data) => {
          if (data.action === picker.Action.CANCEL) { setBusy(''); return; }
          if (data.action !== picker.Action.PICKED) return;
          const ids = (data.docs || []).map((d) => d.id).slice(0, 10);
          setBusy('import');
          try {
            const r = await importFromDrive(tenantId, ids, eventId);
            setFailed(r.failed || []);
            if (r.uploaded && r.uploaded.length) onImported(r.uploaded);
          } catch (err) {
            setFailed(err?.response?.data?.failed || []);
            setError(errorText(err, t('publishing.import.failed', 'The import failed. Try again.')));
          } finally { setBusy(''); }
        });
      if (cfg.appId) builder.setAppId(cfg.appId);
      builder.build().setVisible(true);
    } catch (err) {
      setBusy('');
      setError(errorText(err, t('publishing.import.pickerError', 'Google Drive could not open. Try again.')));
    }
  };

  if (!status) return <p className="pb-ai-meta">{t('publishing.import.loading', 'Loading…')}</p>;
  if (!status.configured) return <div className="note info">{t('publishing.import.driveOff', 'Google Drive import is not set up on this server yet.')}</div>;
  return (
    <div className="pb-import" data-testid="drive-panel">
      {!status.connected ? (
        <>
          <p style={{ marginTop: 0 }}>{t('publishing.import.driveIntro', 'Connect Google Drive, then pick photos or videos. We only see the files you pick.')}</p>
          <button type="button" className="pb-btn primary" onClick={connect}><FaGoogleDrive aria-hidden="true" /> {t('publishing.import.driveConnect', 'Connect Google Drive')}</button>
        </>
      ) : (
        <>
          <p style={{ marginTop: 0 }}>{status.email ? t('publishing.import.connectedAs', 'Connected as {{email}}.', { email: status.email }) : t('publishing.import.connected', 'Connected.')}</p>
          <div className="pb-actions">
            <button type="button" className="pb-btn primary" disabled={!!busy || !eventId} onClick={pick}>
              <FaGoogleDrive aria-hidden="true" /> {busy === 'import' ? t('publishing.import.importing', 'Importing…') : t('publishing.import.drivePick', 'Choose from Google Drive')}
            </button>
            <button type="button" className="pb-btn" onClick={async () => { await disconnectDrive(tenantId).catch(() => {}); load(); }}>{t('publishing.import.disconnect', 'Disconnect')}</button>
          </div>
        </>
      )}
      {error && <div className="note error" role="alert" style={{ marginTop: 8 }}>{error}</div>}
      {failed.length > 0 && (
        <ul className="pb-msgs" style={{ marginTop: 8 }}>{failed.map((f) => <li key={f.fileId || f.message} className="warn">{f.message}</li>)}</ul>
      )}
    </div>
  );
}

function CanvaPanel({ tenantId, eventId, onImported, t }) {
  const [status, setStatus] = useState(null);
  const [designs, setDesigns] = useState(null);
  const [next, setNext] = useState(null);
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState(null);
  const [format, setFormat] = useState('png');
  const [orientation, setOrientation] = useState('vertical');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => getCanvaStatus(tenantId).then(setStatus).catch(() => setStatus({ configured: false })), [tenantId]);
  useEffect(() => { load(); }, [load]);

  const search = useCallback(async (more = false) => {
    setError(''); setBusy('list');
    try {
      const r = await listCanvaDesigns(tenantId, { query, continuation: more ? next : '' });
      setDesigns((d) => (more && d ? [...d, ...r.designs] : r.designs));
      setNext(r.continuation);
    } catch (err) {
      setError(errorText(err, t('publishing.import.canvaListError', 'Could not load your Canva designs.')));
    } finally { setBusy(''); }
  }, [tenantId, query, next, t]);

  useEffect(() => { if (status && status.connected && designs === null) search(false); }, [status, designs, search]);

  const connect = async () => {
    setError('');
    await connectPopup(canvaLoginUrl(tenantId), 'CANVA_CONNECTED');
    setDesigns(null);
    load();
  };

  const doImport = async () => {
    setError(''); setBusy('import');
    try {
      const r = await importFromCanva(tenantId, { designId: chosen.id, title: chosen.title, format, orientation, eventId });
      if (r.uploaded && r.uploaded.length) onImported(r.uploaded);
      setChosen(null);
    } catch (err) {
      setError(errorText(err, t('publishing.import.failed', 'The import failed. Try again.')));
    } finally { setBusy(''); }
  };

  if (!status) return <p className="pb-ai-meta">{t('publishing.import.loading', 'Loading…')}</p>;
  if (!status.configured) return <div className="note info">{t('publishing.import.canvaOff', 'Canva import is not set up on this server yet.')}</div>;
  if (!status.connected) {
    return (
      <div className="pb-import" data-testid="canva-panel">
        <p style={{ marginTop: 0 }}>{t('publishing.import.canvaIntro', 'Connect Canva to bring in your designs as images or videos.')}</p>
        <button type="button" className="pb-btn primary" onClick={connect}><FaPalette aria-hidden="true" /> {t('publishing.import.canvaConnect', 'Connect Canva')}</button>
        {error && <div className="note error" role="alert" style={{ marginTop: 8 }}>{error}</div>}
      </div>
    );
  }
  return (
    <div className="pb-import" data-testid="canva-panel">
      <form className="pb-actions" onSubmit={(e) => { e.preventDefault(); setDesigns(null); setNext(null); search(false); }}>
        <label className="pb-field" htmlFor="pb-canva-q" style={{ flex: '1 1 12rem' }}>
          {t('publishing.import.canvaSearch', 'Search your designs')}
          <input id="pb-canva-q" type="search" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <button type="submit" className="pb-btn" disabled={busy === 'list'}>{t('publishing.import.search', 'Search')}</button>
        <button type="button" className="pb-btn" onClick={async () => { await disconnectCanva(tenantId).catch(() => {}); setDesigns(null); load(); }}>{t('publishing.import.disconnect', 'Disconnect')}</button>
      </form>
      {designs && designs.length === 0 && <p className="pb-ai-meta">{t('publishing.import.canvaNone', 'No designs found.')}</p>}
      {designs && designs.length > 0 && (
        <ul className="pb-designs" style={{ marginTop: 8 }}>
          {designs.map((d) => (
            <li key={d.id}>
              <button type="button" className="pb-design" aria-pressed={chosen && chosen.id === d.id} onClick={() => setChosen(d)}>
                {d.thumbnail ? <img src={d.thumbnail} alt="" loading="lazy" /> : <span className="pb-ai-meta">{t('publishing.import.noPreview', 'No preview')}</span>}
                <span>{d.title}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {next && <button type="button" className="pb-btn" style={{ marginTop: 8 }} disabled={busy === 'list'} onClick={() => search(true)}>{t('publishing.import.more', 'Show more')}</button>}
      {chosen && (
        <div className="pb-actions" style={{ marginTop: 10 }}>
          <label className="pb-field" htmlFor="pb-canva-format">
            {t('publishing.import.format', 'Import as')}
            <select id="pb-canva-format" value={format} onChange={(e) => setFormat(e.target.value)}>
              <option value="png">PNG</option>
              <option value="jpg">JPG</option>
              <option value="mp4">{t('publishing.import.video', 'Video (MP4)')}</option>
            </select>
          </label>
          {format === 'mp4' && (
            <label className="pb-field" htmlFor="pb-canva-orient">
              {t('publishing.import.orientation', 'Shape')}
              <select id="pb-canva-orient" value={orientation} onChange={(e) => setOrientation(e.target.value)}>
                <option value="vertical">{t('publishing.import.vertical', 'Vertical (Reels, TikTok)')}</option>
                <option value="horizontal">{t('publishing.import.horizontal', 'Horizontal')}</option>
              </select>
            </label>
          )}
          <button type="button" className="pb-btn primary" disabled={!!busy || !eventId} onClick={doImport}>
            <FaPalette aria-hidden="true" /> {busy === 'import' ? t('publishing.import.exporting', 'Exporting from Canva…') : t('publishing.import.canvaImport', 'Import “{{title}}”', { title: chosen.title })}
          </button>
        </div>
      )}
      {error && <div className="note error" role="alert" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

export default function MediaImportModal({ tenantId, eventId, onImported, onClose }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('drive');
  const [count, setCount] = useState(0);
  const dialog = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    dialog.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const imported = (files) => { setCount((c) => c + files.length); onImported(files); };

  return (
    <div className="pb-overlay" role="presentation" onClick={onClose}>
      <div className="pb-modal" role="dialog" aria-modal="true" aria-labelledby="pb-import-title" tabIndex={-1} ref={dialog} onClick={(e) => e.stopPropagation()} data-testid="media-import-modal">
        <h3 id="pb-import-title">{t('publishing.import.title', 'Import media')}</h3>
        <div className="pb-tabs" role="tablist">
          <button type="button" role="tab" className="pb-tab" aria-selected={tab === 'drive'} onClick={() => setTab('drive')}><FaGoogleDrive aria-hidden="true" /> Google Drive</button>
          <button type="button" role="tab" className="pb-tab" aria-selected={tab === 'canva'} onClick={() => setTab('canva')}><FaPalette aria-hidden="true" /> Canva</button>
        </div>
        <div role="tabpanel">
          {tab === 'drive'
            ? <DrivePanel tenantId={tenantId} eventId={eventId} onImported={imported} t={t} />
            : <CanvaPanel tenantId={tenantId} eventId={eventId} onImported={imported} t={t} />}
        </div>
        {count > 0 && <div className="note ok" role="status">{t('publishing.import.added', '{{count}} file(s) added to your post.', { count })}</div>}
        <div className="pb-actions" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="pb-btn" onClick={onClose}>{t('publishing.import.close', 'Done')}</button>
        </div>
      </div>
    </div>
  );
}
